import { useVideoCreatorStore } from '../stores-v2/VideoCreatorStoreContext';
import { useStore } from '../stores-v2/StoreContext';
import { SidebarOption } from '../types.ts/general';
import { Timeline } from '../editor/models/Timeline';
import { AddVideoElementCommand } from '../editor/commands/AddVideoElementCommand';
import { AddVideoElementOptions } from '../editor/commands/AddVideoElementCommand';
import { AspectRatio } from '../types.ts/video';
import {
  combineVideoDataPerTrack,
  combineVideoDataFromHeadlessTracks,
  VideoData,
  calculateLogoPosition,
} from '../utility/logoPlacement';
import { PhotoArtifactTab } from '../types.ts/story';
import { runInAction } from 'mobx';
import { v4 as uuid } from 'uuid';
import applyImageOptimizations from '../utility/applyImageOptimizations';
import { getImageWithBlackFrameElements } from '../utility/elements';
import { DEFAULT_ANIMATION_SPEED } from '../config/imageSettings';
import { getRandomFileName } from '../utility/general';
import { deepClone } from '../utility/deepClone';

/**
 * Interface representing a media file item that can be added to the timeline
 */
export type MediaFileItem = {
  id: string;
  src: string;
  title?: string;
  description: string;
  thumbnailUrl?: string;
  duration?: number; // For videos, duration in seconds
  aspectRatio?: number; // Width/height ratio for layout calculations
  alt?: string; // Alt text for accessibility
  slug?: string; // Filename/slug for the asset
  type?: 'artifact' | 'stock' | 'ai'; // Source type for tracking
  attribution?: any; // Attribution data for stock photos
};

export type MediaFilePlaceholder = Omit<MediaFileItem, 'src'>;

/**
 * Interface for AI photo upload data
 */
export interface AIPhotoData {
  type: 'ai';
  url: string;
  fileName: string;
  alt: string;
  title: string;
}

/**
 * Media type for determining which command to use
 */
export type MediaType = 'video' | 'photo' | 'logo';

/**
 * Logo-specific options for timeline addition
 */
export interface LogoToTimelineOptions {
  /**
   * Source aspect ratio for positioning calculations
   */
  sourceAspectRatio: number;

  /**
   * Video aspect ratio for positioning calculations
   */
  videoAspectRatio: AspectRatio;

  /**
   * Logo opacity (default: '40%')
   */
  opacity?: string;

  /**
   * Logo fit mode (default: 'contain')
   */
  fit?: 'cover' | 'contain' | 'fill';
}

/**
 * Options for adding media to timeline
 */
export interface AddMediaToTimelineOptions {
  /**
   * Whether to fit the media duration to the timeline end
   */
  fitToEnd?: boolean;

  /**
   * Default volume for video elements (0-100)
   */
  volume?: number;

  /**
   * How the media should be resized to fit the element ('cover', 'contain', 'fill')
   */
  fit?: 'cover' | 'contain' | 'fill';

  /**
   * Whether video should loop when it reaches the end
   */
  loop?: boolean;

  /**
   * Whether to automatically add fade in/out effects
   */
  autoFade?: boolean;

  /**
   * Custom duration for photos (in seconds)
   */
  photoDuration?: number;

  /**
   * Whether to apply smart crop for images
   */
  smartCrop?: boolean;

  /**
   * Tab context for determining media behavior (logo vs regular photo)
   */
  tab?: PhotoArtifactTab;

  /**
   * For AI photos: search value used for title/alt text
   */
  searchValue?: string;

  /**
   * Opacity for logo elements (as percentage string like '40%')
   */
  opacity?: string;

  /**
   * Custom positioning and sizing for logos
   */
  logoPosition?: {
    x?: string;
    y?: string;
    width?: string;
    height?: string;
    x_anchor?: string;
    y_anchor?: string;
  };

  /**
   * Whether to create composition elements for portrait images
   */
  useComposition?: boolean;

  /**
   * Custom animations to apply to the element
   */
  animations?: any[];

  /**
   * Custom track to place element on
   */
  track?: number;

  /**
   * Custom time position to place element at
   */
  time?: number;

  /**
   * Custom element duration
   */
  duration?: number;
}

const isMediaFileItem = (
  item: MediaFileItem | MediaFilePlaceholder,
): item is MediaFileItem => {
  return 'src' in item && typeof item.src === 'string';
};

/**
 * Hook for adding video/photo elements to the headless editor timeline
 * Handles both headless editor commands and fallback to original implementation
 */
export const useAddMediaToTimeline = () => {
  const videoCreator = useVideoCreatorStore();
  const { editorStore, datoClientStore } = useStore();

  /**
   * Simple timeout-based auto-selection for newly added elements
   * Uses 300ms delay to ensure element is in state before selecting
   */
  const autoSelectElementAfterDelay = (elementId: string) => {
    setTimeout(async () => {
      if (elementId) {
        console.log(`Auto-selecting newly added element: ${elementId}`);
        await videoCreator.setActiveElements(elementId);
      } else {
        console.warn(
          `Could not find newly added element with ID: ${elementId}`,
        );
      }
    }, 300);
  };

  /**
   * Auto-selection for original implementation using element count comparison
   * Uses 300ms delay to ensure element is in state before selecting
   */
  const autoSelectLatestElementAfterDelay = () => {
    const elementCountBefore = videoCreator.state?.elements.length || 0;

    setTimeout(async () => {
      const elementsAfter = videoCreator.state?.elements || [];

      if (elementsAfter.length > elementCountBefore) {
        // Select the most recently added element (last in the array)
        const latestElement = elementsAfter[elementsAfter.length - 1];
        console.log(
          `Auto-selecting latest added element: ${latestElement.source.id}`,
        );
        await videoCreator.setActiveElements(latestElement.source.id);
      }
    }, 300);
  };

  /**
   * Adds a video or photo element to the timeline
   * @param item The media file item to add
   * @param mediaType The type of media ('video', 'photo', or 'logo')
   * @param options Additional options for media handling
   * @returns Promise<boolean> indicating success
   */
  const addMediaToTimeline = async (
    item: MediaFileItem | MediaFilePlaceholder,
    mediaType: MediaType,
    options: AddMediaToTimelineOptions | LogoToTimelineOptions = {},
  ): Promise<boolean> => {
    try {
      // Use headless editor if available, otherwise fall back to original implementation
      if (editorStore && editorStore.headlessEnabled) {
        return await addMediaViaHeadlessEditor(item, mediaType, options);
      } else {
        return await addMediaViaOriginalImplementation(
          item,
          mediaType,
          options,
        );
      }
    } catch (error) {
      console.error(`Failed to add ${mediaType} to timeline:`, error);
      videoCreator.toastState = {
        state: 'error',
        message: `Failed to add ${mediaType}. Please try again.`,
      };
      return false;
    }
  };

  /**
   * Adds media using the headless editor commands
   */
  const addMediaViaHeadlessEditor = async (
    item: MediaFileItem | MediaFilePlaceholder,
    mediaType: MediaType,
    options: AddMediaToTimelineOptions | LogoToTimelineOptions,
  ): Promise<boolean> => {
    if (mediaType === 'video' && isMediaFileItem(item)) {
      return await addVideoViaHeadlessEditor(
        item,
        options as AddMediaToTimelineOptions,
      );
    } else if (mediaType === 'photo') {
      return await addPhotoViaHeadlessEditor(
        item,
        options as AddMediaToTimelineOptions,
      );
    } else if (mediaType === 'logo' && isMediaFileItem(item)) {
      return await addLogoViaHeadlessEditor(
        item,
        options as LogoToTimelineOptions,
      );
    }

    throw new Error(`Unsupported media type: ${mediaType}`);
  };

  /**
   * Adds a video using AddVideoElementCommand
   */
  const addVideoViaHeadlessEditor = async (
    item: MediaFileItem,
    options: AddMediaToTimelineOptions,
  ): Promise<boolean> => {
    // Create video element for headless editor
    const elementProps = {
      name: item.title || item.description || 'Video Element',
      type: 'video',
      source: item.src,
      autoplay: true,
      time: videoCreator.time || 0, // Use current playhead position
      duration: item.duration || 8, // Default to 8 seconds if duration not provided
      fit: options.fit || 'cover',
      loop: options.loop || false,
    };

    // TODO: mapItemToElementProps: (item: MediaFileItem) => {}

    const videoElement = Timeline.createElement('video', elementProps);

    // Prepare video-specific options for the command
    const commandOptions: AddVideoElementOptions = {
      volume: options.volume || 100,
      fit: options.fit || 'cover',
      loop: options.loop || false,
      autoFade: options.autoFade || false,
    };

    // Use AddVideoElementCommand
    const command = editorStore.editor.commandAddVideoElement(
      videoElement,
      undefined, // Let the command auto-select track
      undefined, // Use default timeline mode
      commandOptions,
    );

    if (command) {
      console.log(
        `Video added via headless editor: ${
          elementProps.name
        } to track ${command.getFinalTrackId()}`,
      );
      videoCreator.sidebarOptions = SidebarOption.editing;

      // Auto-select element after delay
      autoSelectElementAfterDelay(videoElement.id);

      return true;
    } else {
      throw new Error('Failed to execute AddVideoElementCommand');
    }
  };

  /**
   * Adds a photo using AddImageElementCommand (when available) or fallback to AddMediaElementCommand
   */
  const addPhotoViaHeadlessEditor = async (
    item: MediaFileItem | MediaFilePlaceholder,
    options: AddMediaToTimelineOptions,
  ): Promise<boolean> => {
    // Handle AI photo upload if needed
    let processedItem = item;
    if (item.type === 'ai' && isMediaFileItem(item)) {
      const uploadedData = await uploadAiPhoto(
        item.src,
        options.searchValue || '',
      );
      if (!uploadedData) {
        throw new Error('Failed to upload AI photo');
      }
      processedItem = {
        ...item,
        src: uploadedData.url || item.src,
        id: uploadedData.id || item.id,
        title: options.searchValue,
        description: options.searchValue || '',
        alt: options.searchValue,
      };
    }

    let optimizedSrc;
    // Apply image optimizations
    if (isMediaFileItem(processedItem)) {
      optimizedSrc = applyImageOptimizations(processedItem.src);
    }

    // Default to cover fit instead of black frame composition
    // Only use composition if explicitly requested via options.useComposition = true
    const useComposition = options.useComposition === true;

    // Set up default animations if not provided
    const defaultAnimations = [
      {
        start_scale: '100%',
        end_scale: DEFAULT_ANIMATION_SPEED,
        x_anchor: '50%',
        y_anchor: '50%',
        fade: false,
        scope: 'element',
        easing: 'linear',
        type: 'scale',
        arbor_subType: 'zoomIn',
      },
    ];

    // Calculate track assignment using getFreeMediaTrack for consistency with original implementation
    const photoDuration = options.duration || options.photoDuration || 4;
    const freeTrack =
      options.track || videoCreator.getFreeMediaTrack('image', photoDuration);

    const elementProps =
      useComposition && optimizedSrc
        ? {
            type: 'composition',
            duration: photoDuration,
            time: options.time || videoCreator.time || 0,
            track: freeTrack,
            source: null,
            elements: getImageWithBlackFrameElements({
              source: optimizedSrc,
            }).map((elementData) =>
              Timeline.createElement(elementData.type as any, elementData),
            ),
            animations: options.animations || defaultAnimations,
          }
        : {
            type: 'image',
            source: optimizedSrc,
            time: options.time || videoCreator.time || 0,
            duration: photoDuration,
            fit: options.fit || 'cover',
            smart_crop: options.smartCrop !== false,
            autoplay: true,
            animations: options.animations || defaultAnimations,
            track: freeTrack,
            // Base Element properties with proper defaults
            x: '50%',
            y: '50%',
            width: '100%',
            height: '100%',
            visible: true,
            locked: false,
            dynamic: false,
          };

    const imageElement = Timeline.createElement(
      elementProps.type as any,
      elementProps,
    );

    // Use the base AddMediaElementCommand for now
    const command = editorStore.editor.commandAddMediaElement(
      imageElement,
      options.track ? options.track.toString() : undefined, // Convert track number to string
      undefined, // Use default timeline mode
    );

    if (command) {
      console.log(
        `Photo added via headless editor: ${
          processedItem.title || processedItem.description || 'Photo Element'
        } to track ${command.getFinalTrackId()}`,
      );

      // Store photo data for Dato if it's stock or AI
      if (
        optimizedSrc &&
        (processedItem.type === 'stock' || processedItem.type === 'ai')
      ) {
        await storePhotoDataForDato({ ...processedItem, src: optimizedSrc });
      }

      videoCreator.sidebarOptions = SidebarOption.editing;

      // Auto-select element after delay
      autoSelectElementAfterDelay(imageElement.id);

      return true;
    } else {
      throw new Error('Failed to execute AddMediaElementCommand for image');
    }
  };

  /**
   * Adds media using the original VideoCreatorStore implementation
   */
  const addMediaViaOriginalImplementation = async (
    item: MediaFileItem | MediaFilePlaceholder,
    mediaType: MediaType,
    options: AddMediaToTimelineOptions | LogoToTimelineOptions,
  ): Promise<boolean> => {
    if (mediaType === 'video' && isMediaFileItem(item)) {
      return await addVideoViaOriginalImplementation(
        item,
        options as AddMediaToTimelineOptions,
      );
    } else if (mediaType === 'photo') {
      return await addPhotoViaOriginalImplementation(
        item,
        options as AddMediaToTimelineOptions,
      );
    } else if (mediaType === 'logo' && isMediaFileItem(item)) {
      return await addLogoViaOriginalImplementation(
        item,
        options as LogoToTimelineOptions,
      );
    }

    throw new Error(`Unsupported media type: ${mediaType}`);
  };

  /**
   * Adds a video using the original VideoCreatorStore.createElement method
   */
  const addVideoViaOriginalImplementation = async (
    item: MediaFileItem,
    options: AddMediaToTimelineOptions,
  ): Promise<boolean> => {
    const videoDuration = item.duration || 8;
    const freeTrack = videoCreator.getFreeMediaTrack('video', videoDuration);

    const elementSource: Record<string, any> = {
      type: 'video',
      source: item.src,
      duration: `${videoDuration} s`,
      autoplay: true,
      fit: options.fit || 'cover',
      loop: options.loop || false,
      ...(freeTrack && { track: freeTrack }),
    };

    // Apply volume if specified
    if (options.volume !== undefined) {
      elementSource.volume = `${options.volume}%`;
    }

    // Set up auto-selection for latest element after delay
    autoSelectLatestElementAfterDelay();

    await videoCreator.createElement(
      elementSource,
      true,
      freeTrack ? undefined : videoCreator.pickOriginalVideoTrack,
    );

    videoCreator.sidebarOptions = SidebarOption.editing;
    return true;
  };

  /**
   * Adds a photo using the original VideoCreatorStore.createElement method
   */
  const addPhotoViaOriginalImplementation = async (
    item: MediaFileItem | MediaFilePlaceholder,
    options: AddMediaToTimelineOptions,
  ): Promise<boolean> => {
    // Handle AI photo upload if needed
    let processedItem = item;
    if (item.type === 'ai' && isMediaFileItem(item)) {
      const uploadedData = await uploadAiPhoto(
        item.src,
        options.searchValue || '',
      );
      if (!uploadedData) {
        throw new Error('Failed to upload AI photo');
      }
      processedItem = {
        ...item,
        src: uploadedData.url || item.src,
        id: uploadedData.id || item.id,
        title: options.searchValue,
        description: options.searchValue || '',
        alt: options.searchValue,
      };
    }

    let optimizedSrc;
    if (isMediaFileItem(processedItem)) {
      optimizedSrc = applyImageOptimizations(processedItem.src);
    }
    const photoDuration = options.photoDuration || 4;
    const freeTrack =
      options.track || videoCreator.getFreeMediaTrack('image', photoDuration);

    // Default to cover fit instead of black frame composition
    // Only use composition if explicitly requested via options.useComposition = true
    const useComposition = options.useComposition === true;

    // Set up default animations
    const defaultAnimations = [
      {
        start_scale: '100%',
        end_scale: DEFAULT_ANIMATION_SPEED,
        x_anchor: '50%',
        y_anchor: '50%',
        fade: false,
        scope: 'element',
        easing: 'linear',
        type: 'scale',
        arbor_subType: 'zoomIn',
      },
    ];

    const elementSource: Record<string, any> =
      useComposition && optimizedSrc
        ? {
            type: 'composition',
            duration: `${photoDuration} s`,
            ...(freeTrack && { track: freeTrack }),
            source: null,
            elements: getImageWithBlackFrameElements({ source: optimizedSrc }),
            animations: options.animations || defaultAnimations,
          }
        : {
            type: 'image',
            source: optimizedSrc,
            duration: `${photoDuration} s`,
            autoplay: true,
            fit: options.fit || 'cover',
            smart_crop: options.smartCrop !== false,
            animations: options.animations || defaultAnimations,
            ...(freeTrack && { track: freeTrack }),
          };

    // Set up auto-selection for latest element after delay
    autoSelectLatestElementAfterDelay();

    await videoCreator.createElement(elementSource);

    // Store photo data for Dato if it's stock or AI
    if (
      optimizedSrc &&
      (processedItem.type === 'stock' || processedItem.type === 'ai')
    ) {
      await storePhotoDataForDato({ ...processedItem, src: optimizedSrc });
    }

    videoCreator.sidebarOptions = SidebarOption.editing;
    return true;
  };

  /**
   * Adds a logo using headless editor commands
   */
  const addLogoViaHeadlessEditor = async (
    item: MediaFileItem, // PhotoAsset
    options: AddMediaToTimelineOptions, // LogoToTimelineOptions
  ): Promise<boolean> => {
    const optimizedSrc = applyImageOptimizations(item.src);
    const sourceAspectRatio = item.aspectRatio || 1;
    const logoId = uuid();

    // Store logo metadata
    videoCreator.currentVideo!.extraElementData = {
      ...(videoCreator.currentVideo?.extraElementData || {}),
      [`logo_el_${logoId}`]: {
        isLogo: true,
      },
    };

    // Get transcript track for filtering
    const transcriptTrack = editorStore.editor.getTranscriptTrack();

    let time = 0;
    let duration = null;

    // Get video timing data from editor.timeline using headless-specific function
    const tracks = editorStore.editor.timeline.tracks;
    const videoDataPerTrack = combineVideoDataFromHeadlessTracks(
      tracks,
      transcriptTrack?.id,
    );

    if (videoDataPerTrack.length) {
      videoDataPerTrack.sort((a, b) => (b.duration || 0) - (a.duration || 0));
      ({ time, duration } = videoDataPerTrack[0]);
    }

    let track = options.track;
    if (!track) {
      track = videoCreator.getMaxTrack() + 1;
    }

    // Calculate logo positioning based on aspect ratio
    const logoPosition = calculateLogoPosition(
      sourceAspectRatio,
      videoCreator.currentVideo?.aspectRatio,
    );

    const elementProps = {
      id: logoId,
      type: 'image',
      source: optimizedSrc,
      duration,
      time,
      track,
      opacity: options.opacity || '40%',
      fit: 'contain',
      ...logoPosition,
      visible: true,
      locked: false,
      dynamic: false,
    };

    const logoElement = Timeline.createElement('image', elementProps);

    const command = editorStore.editor.commandAddMediaElement(
      logoElement,
      track ? track.toString() : undefined, // Convert track number to string
      undefined,
    );

    if (command) {
      console.log(
        `Logo added via headless editor: ${
          item.title || item.description || 'Logo Element'
        } to track ${command.getFinalTrackId()}`,
      );
      videoCreator.sidebarOptions = SidebarOption.editing;
      // Auto-select element after delay
      autoSelectElementAfterDelay(logoElement.id);
      return true;
    } else {
      throw new Error('Failed to execute AddMediaElementCommand for logo');
    }
  };

  /**
   * Adds a logo using the original implementation
   */
  const addLogoViaOriginalImplementation = async (
    item: MediaFileItem,
    options: AddMediaToTimelineOptions,
  ): Promise<boolean> => {
    const optimizedSrc = applyImageOptimizations(item.src);
    const sourceAspectRatio = item.aspectRatio || 1;
    const logoId = uuid();

    videoCreator.currentVideo!.extraElementData = {
      ...(videoCreator.currentVideo?.extraElementData || {}),
      [`logo_el_${logoId}`]: {
        isLogo: true,
      },
    };

    let time = 0;
    let duration = null;
    const videoDataPerTrack = combineVideoDataPerTrack(
      videoCreator.tracks!,
      (el) => videoCreator.isOriginalVideoElement(el.source),
    );
    if (videoDataPerTrack.length) {
      videoDataPerTrack.sort((a, b) => b.duration - a.duration);
      ({ time, duration } = videoDataPerTrack[0]);
    }

    const logoPosition = calculateLogoPosition(
      sourceAspectRatio,
      videoCreator.currentVideo?.aspectRatio,
    );

    // Set up auto-selection for latest element after delay
    autoSelectLatestElementAfterDelay();

    await videoCreator.createElement({
      id: logoId,
      type: 'image',
      source: optimizedSrc,
      duration,
      time,
      track: videoCreator.getMaxTrack() + 1,
      opacity: options.opacity || '40%',
      fit: 'contain',
      ...logoPosition,
    });

    videoCreator.sidebarOptions = SidebarOption.editing;
    return true;
  };

  /**
   * Uploads AI photo and returns upload data
   */
  const uploadAiPhoto = async (url: string, searchValue: string) => {
    try {
      if (!datoClientStore) return null;

      const fileName = getRandomFileName(searchValue);

      const newPhotoData: AIPhotoData = {
        type: 'ai',
        url,
        fileName,
        alt: searchValue,
        title: searchValue,
      };

      const newUpload =
        await datoClientStore.assetRepository?.uploadFile(newPhotoData);
      return { url: newUpload?.url, id: newUpload?.id, fileName };
    } catch (error) {
      console.error('Error uploading AI photo:', error);
      return null;
    }
  };

  /**
   * Store photo data for Dato CMS
   */
  const storePhotoDataForDato = async (item: MediaFileItem) => {
    if (item.type === 'stock' || item.type === 'ai') {
      runInAction(() => {
        videoCreator.photoDataForDato = {
          ...videoCreator.photoDataForDato,
          [item.id]: {
            type: item.type!,
            url: item.src,
            fileName: item.slug || item.title || 'untitled',
            title: item.title || item.description,
            alt: item.alt || item.description,
          },
        };
      });
    }
  };

  return {
    addMediaToTimeline,
    addLogoViaHeadlessEditor,
    addLogoViaOriginalImplementation,
  };
};

export default useAddMediaToTimeline;
